import { useEffect, useId, useRef, useState } from 'react';
import { Check, Globe, Search, X } from 'lucide-react';
import type { Country } from '../types.js';
import { searchCountries } from '../../../../packages/shared/src/countries-search.js';

export function Flag({ code }: { code: string }) {
  return code ? (
    <span className={'country-flag fi fi-' + code.toLowerCase()} aria-hidden="true" />
  ) : (
    <Globe size={18} aria-hidden />
  );
}

// A type-to-search country field: the input is the search box, and matches open directly beneath it.
export function CountryCombobox({
  country,
  countries,
  onChange,
  label,
  featured,
  hint,
  className,
}: {
  country: string;
  countries: Country[];
  onChange: (code: string) => void;
  label: string;
  // Countries listed first, under their own heading, until the visitor starts typing.
  featured?: { codes: readonly string[]; label: string; restLabel: string };
  // Short note shown beside a country, such as how many providers deliver there.
  hint?: (code: string) => string | undefined;
  className?: string;
}) {
  const [open, setOpen] = useState(false),
    [query, setQuery] = useState(''),
    [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const id = useId();
  const selected = countries.find((c) => c.code === country);

  const arrange = (text: string) => {
    const matches = searchCountries(countries, text);
    if (!featured || text.trim()) return { top: [] as Country[], rest: matches };
    const codes = new Set(featured.codes);
    return { top: matches.filter((c) => codes.has(c.code)), rest: matches.filter((c) => !codes.has(c.code)) };
  };
  const { top, rest } = arrange(query);
  const options = [...top, ...rest];

  const show = () => {
    if (open) return;
    const initial = arrange('');
    setQuery('');
    setActive(Math.max(0, [...initial.top, ...initial.rest].findIndex((c) => c.code === country)));
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setQuery('');
  };
  const choose = (code: string) => {
    onChange(code);
    close();
    // Keep focus here with the name selected, so typing again starts a fresh search.
    requestAnimationFrame(() => input.current?.select());
  };

  useEffect(() => {
    if (open) document.getElementById(id + '-option-' + active)?.scrollIntoView({ block: 'nearest' });
  }, [open, active, id]);

  const renderOption = (option: Country, index: number) => {
    const note = hint?.(option.code);
    return (
      <div
        role="option"
        id={id + '-option-' + index}
        key={option.code}
        aria-selected={country === option.code}
        className={'combobox-option' + (index === active ? ' is-active' : '')}
        onPointerMove={() => setActive(index)}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => choose(option.code)}
      >
        <Flag code={option.code} />
        <span className="combobox-option-name">{option.name}</span>
        {note && <span className="combobox-option-hint">{note}</span>}
        {country === option.code && <Check size={16} aria-hidden />}
      </div>
    );
  };

  return (
    <div className={'country-combobox' + (className ? ' ' + className : '')} onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) close(); }}>
      <div className={'combobox-field' + (open ? ' is-open' : '')} onMouseDown={(event) => { if (event.target !== input.current) { event.preventDefault(); input.current?.focus(); show(); } }}>
        <Flag code={selected?.code ?? ''} />
        <input
          ref={input}
          id={id + '-input'}
          role="combobox"
          aria-label={label}
          aria-expanded={open}
          aria-controls={id + '-list'}
          aria-autocomplete="list"
          aria-activedescendant={open && options[active] ? id + '-option-' + active : undefined}
          autoComplete="off"
          spellCheck={false}
          placeholder={open ? selected?.name ?? 'Search countries…' : 'Search countries…'}
          value={open ? query : selected?.name ?? ''}
          onFocus={show}
          onClick={show}
          onChange={(event) => {
            if (!open) setOpen(true);
            setQuery(event.target.value);
            setActive(0);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              if (!open) return show();
              setActive((index) => Math.max(0, Math.min(options.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1))));
            } else if (event.key === 'Enter' && open && options[active]) {
              event.preventDefault();
              choose(options[active].code);
            } else if (event.key === 'Escape' && open) {
              event.preventDefault();
              event.stopPropagation();
              close();
            }
          }}
        />
        {open && query ? (
          <button type="button" className="combobox-clear" aria-label="Clear search" onMouseDown={(event) => event.preventDefault()} onClick={() => { setQuery(''); setActive(0); input.current?.focus(); }}>
            <X size={16} aria-hidden />
          </button>
        ) : (
          <Search className="combobox-icon" size={17} aria-hidden />
        )}
      </div>
      {open && (
        <div className="combobox-popover">
          <div className="combobox-list" role="listbox" aria-label={label} id={id + '-list'}>
            {top.length > 0 && featured ? (
              <>
                <div role="group" aria-labelledby={id + '-featured'}>
                  <div className="combobox-group-label" id={id + '-featured'} role="presentation">{featured.label}</div>
                  {top.map((option, index) => renderOption(option, index))}
                </div>
                <div role="group" aria-labelledby={id + '-rest'}>
                  <div className="combobox-group-label" id={id + '-rest'} role="presentation">{featured.restLabel}</div>
                  {rest.map((option, index) => renderOption(option, top.length + index))}
                </div>
              </>
            ) : (
              options.map((option, index) => renderOption(option, index))
            )}
          </div>
          {!options.length && <p className="combobox-empty" role="status">No countries match “{query}”. Try another name.</p>}
        </div>
      )}
    </div>
  );
}
